import { api, type Execution, type Issuable } from '../api';
import { callout, close, field, input, opt, openDialog, select, val, numVal } from '../dialog';
import { KIND_LABEL, TAG_STYLE, expiryText } from '../stock-ui';
import { esc, html, toast } from '../ui';

/**
 * Taking parts out of the store and onto a work pack.
 *
 * Everything unfittable is listed with the reason rather than left out. An
 * engineer standing at the bench holding a tin that does not appear in the
 * picker learns nothing; one that appears greyed out saying "expired
 * 01.03.2022" learns exactly what to do next.
 *
 * The refusals themselves live in the API — expired, uninspected, condemned,
 * no acceptable paperwork, tagged to another aeroplane — because a check that
 * only exists in a dialog is a check that a second caller skips.
 */
export function issuePartsDialog(e: Execution, reload: () => void): void {
  let data: Issuable | null = null;
  let search = '';
  let chosen: string | null = null;

  const load = async (refresh: () => void) => {
    data = await api.stores.issuable(e.id);
    refresh();
  };

  /**
   * The list on its own, so that typing can rewrite it without rewriting the
   * box being typed into. It used to re-render the whole dialog on every
   * keystroke, which put the caret back at the start of the box — searching
   * for "oil" typed "lio".
   */
  const lotRows = (): string => {
    const q = search.trim().toLowerCase();
    const rows = (data?.lots ?? []).filter(
      (l) =>
        !q ||
        l.partNumber.toLowerCase().includes(q) ||
        l.description.toLowerCase().includes(q) ||
        (l.batchLot ?? '').toLowerCase().includes(q),
    );
    if (!rows.length) {
      return '<label><span class="pb"><span class="pt">Nothing on the shelf matches</span></span></label>';
    }
    return rows
      .map(
        (l) => html`
          <label style="${l.blocked ? 'opacity:.55' : ''}">
            <input type="radio" name="lot" value="${esc(l.id)}" data-lot-pick
                   ${l.id === chosen ? 'checked' : ''} ${l.blocked ? 'disabled' : ''}>
            <span class="pb">
              <span class="pt">
                <span style="font-family:var(--mono)">${esc(l.partNumber)}</span>
                ${esc(l.description)}
                ${l.tag ? `<span class="chip" style="${TAG_STYLE[l.tag]}">${esc(l.stateLabel)}</span>` : ''}
              </span>
              <span class="pm">
                ${esc(KIND_LABEL[l.kind] ?? l.kind)} ·
                ${esc(String(l.quantity))} ${esc(l.unit)} on the shelf
                ${l.batchLot ? ` · batch ${esc(l.batchLot)}` : ''}
                ${l.serialNumber ? ` · S/N ${esc(l.serialNumber)}` : ''}
                ${l.effectiveExpiry ? ` · ${esc(expiryText(l))}` : ''}
              </span>
              ${l.blocked ? `<span class="pm" style="color:var(--caution)">${esc(l.blocked)}</span>` : ''}
            </span>
          </label>
        `,
      )
      .join('');
  };

  openDialog<Record<string, unknown>>({
    wide: true,
    title: 'Issue parts from the store',
    sub: `${e.reference} — ${e.registration}`,
    primary: 'Issue it',
    body: () => {
      if (!data) return html`<p class="muted tiny" style="padding:20px">Loading the shelf…</p>`;

      const lot = data.lots.find((l) => l.id === chosen);

      return html`
        ${field('Find it', input('search', search, 'placeholder="part no., description, batch…" data-search'))}

        <div class="pick" data-lot-list style="max-height:290px;overflow:auto">${lotRows()}</div>

        ${lot
          ? html`
              <div class="frow" style="margin-top:10px">
                ${field('How much', input('quantity', '1', 'inputmode="decimal" style="text-align:right;font-family:var(--mono)"'),
                  `${lot.quantity} ${lot.unit} available`)}
                ${field('Fitted by', select('personId', [opt('', 'Not recorded'), ...e.certifyingStaff.map((s) => opt(s.id, s.displayName))].join('')))}
              </div>
              ${lot.sourceAircraft
                ? callout('info', 'i', html`
                    This is the part removed from <b>${esc(lot.sourceAircraft.registration)}</b> — issuing
                    it here records it as refitted to its own aircraft.
                  `)
                : ''}
            `
          : callout('info', 'i', html`
              Pick a lot. Anything greyed out cannot be fitted, and says why: expired material, a lot
              still in quarantine, one with no acceptable release document, or a part tagged to a
              different aircraft. None of those is a judgement the store leaves to the bench.
            `)}
      `;
    },
    onRender: (root, refresh) => {
      if (!data) {
        void load(refresh);
        return;
      }
      const list = root.querySelector<HTMLElement>('[data-lot-list]');
      const bindPicks = () => {
        list?.querySelectorAll<HTMLInputElement>('[data-lot-pick]').forEach((r) =>
          r.addEventListener('change', () => {
            chosen = r.value;
            // Refreshed so the quantity field appears against the lot that was
            // picked, with that lot's own limit under it. A pick is one event,
            // not one per keystroke, so re-rendering here costs nothing.
            refresh();
          }),
        );
      };
      bindPicks();

      const box = root.querySelector<HTMLInputElement>('[data-search]');
      if (box) {
        if (search) {
          // The caret goes back where it was — the value survives a re-render
          // (picking a lot), and landing at position 0 is what typed backwards.
          box.focus();
          box.setSelectionRange(search.length, search.length);
        }
        box.addEventListener('input', () => {
          search = box.value;
          if (list) {
            list.innerHTML = lotRows();
            bindPicks();
          }
        });
      }
    },
    collect: (root) => {
      if (!chosen) return { error: 'Pick the lot it is coming out of.' };
      const lot = data?.lots.find((l) => l.id === chosen);
      const quantity = numVal(root, 'quantity');
      if (!quantity || quantity <= 0) return { error: 'How much is going out?' };
      if (lot && quantity > lot.quantity) {
        return { error: `Only ${lot.quantity} ${lot.unit} of that lot is on the shelf.` };
      }
      return {
        lotId: chosen,
        workOrderId: e.id,
        quantity,
        personId: val(root, 'personId') || undefined,
      };
    },
    submit: (body) => api.stores.issue(body),
    done: (r) => `${r.partNumber} issued to ${e.reference}`,
    onDone: () => {
      close();
      reload();
    },
  });
}

/** Unused material going back. */
export function returnPartDialog(
  e: Execution,
  part: Execution['parts'][number],
  reload: () => void,
): void {
  openDialog<Record<string, unknown>>({
    title: 'Return it to the store',
    sub: `${part.partNumber} — ${part.quantity} ${part.unit} issued`,
    primary: 'Return it',
    body: () => html`
      ${field('How much is coming back', input('quantity', String(part.quantity),
        'inputmode="decimal" style="text-align:right;font-family:var(--mono)"'))}
      ${field('Note', input('note', '', 'maxlength="1000"'))}
      ${callout('info', 'i', html`
        More cannot come back than went out. What returns goes onto the same lot it came from, so its
        batch and its shelf life follow it — a return that created a new nameless lot would lose both.
      `)}
    `,
    collect: (root) => {
      const quantity = numVal(root, 'quantity');
      if (!quantity || quantity <= 0) return { error: 'How much is coming back?' };
      if (!part.stockLotId) return { error: 'This part never came out of the store, so it cannot go back into it.' };
      return {
        lotId: part.stockLotId,
        workOrderId: e.id,
        quantity,
        // Named, so what comes back comes off this line rather than off
        // whichever line of the same lot the API would have picked.
        workOrderPartId: part.id,
        note: val(root, 'note') || undefined,
      };
    },
    submit: (body) => api.stores.returnToStore(body),
    done: (r) => `Returned — ${r.quantity} ${r.unit} on the shelf`,
    onDone: reload,
  });
}

/** Warns rather than blocks: it is the engineer's call, not the screen's. */
export function warnIfNoParts(e: Execution): void {
  if (!e.parts.length) toast('No parts have been issued to this pack.', 'error');
}
